import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { parseNutrition } from '~/types/Product';
import type { ProductPropertiesValue } from '~/types/Product/interface';

const SUB_ITEM_MAP: Record<string, string> = {
  fat: 'fat_saturates',
  carbohydrates: 'sugar',
};

/** Nutrition table rows of the product page, with saturates and sugar nested under their parent values. */
export const useProductNutritionList = (
  propertyList: ProductPropertiesValue[],
) => {
  const { t } = useTranslation();

  return useMemo(() => {
    const items = parseNutrition(propertyList);
    const subItemCodes = new Set(Object.values(SUB_ITEM_MAP));

    const formatValue = (val: string, additionalVal: string | undefined) =>
      additionalVal ? `${additionalVal}\n${val}` : val;

    return items
      .filter((item) => !subItemCodes.has(item.name))
      .map((item) => {
        const subItemCode = SUB_ITEM_MAP[item.name];
        const subItem = subItemCode
          ? items.find((i) => i.name === subItemCode)
          : undefined;

        return {
          code: item.name,
          name: t(`productProperties:${item.name}`),
          value: formatValue(item.value, item.additionalValue),
          extraValue: item.ratio,
          ...(subItem && {
            subItems: [
              {
                name: t(`nutritionTableNames:${subItem.name}`),
                value: formatValue(subItem.value, subItem.additionalValue),
                extraValue: subItem.ratio,
              },
            ],
          }),
        };
      });
  }, [propertyList, t]);
};
